import { ENDPOINTS } from '../../api/endPoints';

export interface CampaignMediaData {
  id: string;
  accountId: string;
  restaurantId: string;
  taskType: string;
  lifecycle: string;
  stage: string;
  message: string;
  metadata: any;
  createdAt: string;
  updatedAt: string;
  percentCompleted: number;
}

export interface CampaignMonitorEvent {
  type: 'connected' | 'progress' | 'done' | 'error';
  message?: string;
  data?: CampaignMediaData;
  ideaId?: number;
  ideaType?: string;
  restaurantId?: string;
  timestamp?: number;
}

export type CampaignStatusCallback = (event: CampaignMonitorEvent) => void;

/**
 * Monitor campaign generation status using Server-Sent Events
 * @param restaurantId - The restaurant ID
 * @param monitorId - The request ID or task ID to monitor
 * @param ideaType - The idea type (e.g., 'MANUAL_IDEA') - optional
 * @param onStatusUpdate - Callback for status updates
 * @param onError - Callback for errors
 * @returns Cleanup function to close the connection
 */
export const monitorCampaignGeneration = (
  restaurantId: string,
  monitorId: number | string,
  ideaType: string | undefined,
  onStatusUpdate: CampaignStatusCallback,
  onError?: (error: Error) => void
): (() => void) => {
  const url = ENDPOINTS.sseMonitor(String(monitorId));
  
  const eventSource = new EventSource(url);

  eventSource.onopen = () => {
    console.log('[Campaign Monitor] Connection established');
  };

  eventSource.addEventListener('connected', (event) => {
    try {
      const data = JSON.parse((event as MessageEvent).data);
      onStatusUpdate({
        type: 'connected',
        message: data.message,
        ideaId: data.ideaId,
        ideaType: data.ideaType,
        restaurantId: data.restaurantId,
        timestamp: data.timestamp,
      });
    } catch (err) {
      console.error('[Campaign Monitor] Failed to parse connected event:', err);
    }
  });

  // Listen for both 'task-update' (old) and 'inprogress' (new) events
  eventSource.addEventListener('task-update', (event) => {
    try {
      const parsed = JSON.parse((event as MessageEvent).data);
      const data: CampaignMediaData = Array.isArray(parsed) ? parsed[0] : parsed;
      
      onStatusUpdate({
        type: 'progress',
        data: data,
      });
    } catch (err) {
      console.error('[Campaign Monitor] Failed to parse task-update event:', err);
    }
  });

  eventSource.addEventListener('inprogress', (event) => {
    try {
      const parsed = JSON.parse((event as MessageEvent).data);
      const data: CampaignMediaData = parsed;
      
      onStatusUpdate({
        type: 'progress',
        data: data,
      });
    } catch (err) {
      console.error('[Campaign Monitor] Failed to parse inprogress event:', err);
    }
  });

  eventSource.addEventListener('done', (event) => {
    try {
      const eventData = JSON.parse((event as MessageEvent).data);
      onStatusUpdate({
        type: 'done',
        message: eventData.message,
        data: eventData.data,
      });
      eventSource.close();
    } catch (err) {
      console.error('[Campaign Monitor] Failed to parse done event:', err);
      eventSource.close();
    }
  });

  eventSource.addEventListener('error', (event) => {
    onStatusUpdate({
      type: 'error',
      message: 'Error occurred during campaign generation monitoring',
    });
  });

  eventSource.onerror = (error) => {
    console.error('[Campaign Monitor] Connection error occurred', error);
    if (onError) {
      onError(new Error('Campaign monitor connection error'));
    }
  };

  return () => {
    eventSource.close();
  };
};

export const isCampaignGenerationComplete = (data: CampaignMediaData): boolean => {
  return data.lifecycle === 'Completed' || data.percentCompleted === 100;
};

export const hasCampaignGenerationFailed = (data: CampaignMediaData): boolean => {
  return data.lifecycle === 'Failed';
};

export const formatCampaignStatus = (stage: string): string => {
  if (!stage) return '';
  return stage.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};
